export type MerchantStatus = 'active' | 'inactive' | 'pending' | 'suspended' | 'blocked'

// ─── Merchant services ────────────────────────────────────────────────────────

export type MerchantService =
  | 'billing'       // Invoices, Quotes, Billing Schedules, Subscriptions, UBB
  | 'pos'           // In-store, Terminals, Inventory, Orders
  | 'ecommerce'     // Ecommerce / Checkout Configs
  | 'payment-links' // Payment Links
  | 'sonar'         // Sonar rules engine
  | 'engagement'    // Engagement
  | 'performance'   // Payments Performance analytics
  | 'cost'          // Payments Cost analytics
  | 'opportunities' // Payments Opportunities analytics
  | 'bins'          // BIN Lookup
  | 'tokens'        // Native / Network Tokens

export interface MerchantServiceMeta {
  label: string
  description: string
  group: 'analytics' | 'channels' | 'risk' | 'billing'
}

export const MERCHANT_SERVICE_META: Record<MerchantService, MerchantServiceMeta> = {
  billing: {
    label: 'Billing',
    description: 'Invoices, quotes, billing schedules, subscriptions and usage-based billing.',
    group: 'billing',
  },
  pos: {
    label: 'POS / In-store',
    description: 'Terminal management, in-store orders and inventory.',
    group: 'channels',
  },
  ecommerce: {
    label: 'Ecommerce',
    description: 'Checkout configurations and hosted payment pages.',
    group: 'channels',
  },
  'payment-links': {
    label: 'Payment Links',
    description: 'Create and share shareable payment links.',
    group: 'channels',
  },
  sonar: {
    label: 'Sonar',
    description: 'Fraud detection rules and transaction screening.',
    group: 'risk',
  },
  engagement: {
    label: 'Engagement',
    description: 'Customer engagement tools and campaigns.',
    group: 'channels',
  },
  performance: {
    label: 'Performance Analytics',
    description: 'Approval rates, declines, chargebacks and dispute trends.',
    group: 'analytics',
  },
  cost: {
    label: 'Cost Analytics',
    description: 'Interchange, effective rate and fee breakdown.',
    group: 'analytics',
  },
  opportunities: {
    label: 'Opportunities',
    description: 'Network tokenization and debit routing optimisation.',
    group: 'analytics',
  },
  bins: {
    label: 'BIN Lookup',
    description: 'Card BIN intelligence and issuer data.',
    group: 'analytics',
  },
  tokens: {
    label: 'Tokens',
    description: 'Native tokens, network tokens and account updater.',
    group: 'analytics',
  },
}

export const ALL_MERCHANT_SERVICES = Object.keys(MERCHANT_SERVICE_META) as MerchantService[]
export type MerchantType = 'individual' | 'business' | 'enterprise'
export type MerchantRiskLevel = 'low' | 'medium' | 'high'
export type ApplicationStatus = 'pending' | 'approved' | 'rejected'
export type CreationMethod = 'application' | 'manual' | 'link'

export interface Merchant {
  id: string
  name: string
  email: string
  status: MerchantStatus
  type: MerchantType
  created: Date
  lastActivity?: Date
  country: string
  industry: string
  monthlyVolume: number
  riskLevel: MerchantRiskLevel
  verified: boolean
  flags?: string[]
  enabledServices?: MerchantService[]
  [key: string]: unknown
}

export interface Address {
  street?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
}

export interface PhoneNumber {
  countryCode?: string
  number?: string
  fullNumber?: string
}

export interface BankAccount {
  currency?: string
  iban?: string
  confirmIban?: string
}

export interface MerchantApplication {
  id: string
  businessName: string
  applicantEmail: string
  submittedDate: string
  status: ApplicationStatus
}

export interface BusinessOwner {
  id: string
  name: string
  ownership: number
}

export interface BusinessAddress {
  street1?: string
  street2?: string
  city?: string
  state?: string
  county?: string
  zipCode?: string
  postcode?: string
  country?: string
}

export interface MerchantBusinessInfo {
  legalBusinessName?: string
  tradingName?: string
  uniqueId?: string
  industry?: string
  website?: string
  productDescription?: string
  businessType?: string
  businessLocation?: string
  businessAddress?: BusinessAddress
}

export interface BusinessExecutive {
  id: string
  name: string
  title: string
  email: string
}

export interface MerchantContactInfo {
  representativeFirstName?: string
  representativeLastName?: string
  representativeEmail?: string
  representativePhone?: PhoneNumber
  businessPhone?: PhoneNumber
  jobTitle?: string
  dateOfBirth?: string
  homeAddress?: Address
  ownershipPercentage?: string
  owns25Percent?: boolean
  hasManagementControl?: boolean
  customerEmail?: string
  businessExecutives?: BusinessExecutive[]
  businessOwners: BusinessOwner[]
}

export interface MerchantBankingInfo {
  currency?: string
  iban?: string
  bankAccount?: BankAccount
}

export interface MerchantSettings {
  statementDescriptor?: string
  shortenedDescriptor?: string
  supportPhone?: string
  showPhoneOnReceipts?: boolean
  selectedProgram?: string
  selectedProduct?: string
  programId?: string
  productId?: string
  capabilities: string[]
  enabledServices: MerchantService[]
  creationMethod?: CreationMethod
  selectedApplicationId?: string
}

export interface MerchantFormData {
  // Program & product selection (from ProgramStep, ProductStep, ProgramProductStep)
  selectedProgram?: string
  selectedProduct?: string
  capabilities: string[]

  // Creation method (from CreationMethodStep)
  creationMethod?: CreationMethod
  selectedApplicationId?: string
  customerEmail?: string

  // Business type & location (from BusinessTypeStep)
  businessLocation?: string
  businessType?: string

  // Business details (from BusinessDetailsStep)
  legalBusinessName?: string
  tradingName?: string
  uniqueId?: string
  industry?: string
  website?: string
  productDescription?: string

  // Business address (from BusinessDetailsStep)
  businessAddress?: BusinessAddress

  // Business phone (from BusinessDetailsStep)
  businessPhone?: PhoneNumber

  // Representative / contact info (from RepresentativeStep)
  representativeFirstName?: string
  representativeLastName?: string
  representativeEmail?: string
  jobTitle?: string
  dateOfBirth?: string
  ownershipPercentage?: string
  owns25Percent?: boolean
  hasManagementControl?: boolean
  representativePhone?: PhoneNumber
  homeAddress?: Address

  // Owners & executives (from OwnersStep, ExecutivesStep)
  // businessOwners is required (non-optional) so that OwnersStep can use
  // `keyof typeof formData.businessOwners[0]` as a type expression safely.
  businessOwners: BusinessOwner[]
  businessExecutives?: BusinessExecutive[]

  // Public / statement details (from PublicDetailsStep)
  statementDescriptor?: string
  shortenedDescriptor?: string
  supportPhone?: string
  showPhoneOnReceipts?: boolean

  // Bank account (from BankDetailsStep)
  bankAccount?: BankAccount

  // Top-level banking fields used in ReviewStep display
  currency?: string
  iban?: string

  // Enabled services
  enabledServices?: MerchantService[]

  // Program/product IDs (used in MerchantSettings-style contexts)
  programId?: string
  productId?: string
}

export interface StepComponentProps {
  formData: MerchantFormData
  updateFormData: (updates: Partial<MerchantFormData>) => void
  onNext?: () => void
  onBack?: () => void
}
